import lodash from 'lodash'

import { TableTypeSlug } from '../../models/companies'

/**
 * Resolves the identifier accessor + table slug a data table uses when pushing
 * its selected rows to a campaign (FIR-3963).
 *
 * Two shapes feed the shared assign endpoint:
 *
 * - Chain / company tables push the chain proxy (ContactCompany) id straight to
 *   the chain assign endpoint (e.g. `restaurants/chains/campaigns/assign/`).
 * - Doors tables push the door's own id to the door-scoped assign endpoint
 *   (e.g. `places/lodging-doors/campaigns/assign/`); the backend
 *   `AssignToCampaignViewSetMixin` resolves each door to its parent chain proxy
 *   before creating the membership, so one door = one membership on its chain,
 *   identical to assigning the chain directly.
 *
 * The previous inline gating excluded doors entirely and, on the chain path,
 * routed the assign request with `companyTypeSlug` instead of `tableTypeSlug`.
 * Both are fixed here: the slug is always the table slug, and doors use their
 * door-id accessor.
 */
export interface CampaignPushTargetArgs {
  isDoorsTable?: boolean
  tableTypeSlug: TableTypeSlug
  /** Accessor for the chain proxy id on a row. Defaults to "id". */
  chainProxyIdAccessorKey?: string
  /** Accessor for the door proxy id on a row. Defaults to "id". */
  doorProxyIdAccessorKey?: string
}

export interface CampaignPushTarget {
  /** lodash path used to read the id sent in include_ids/exclude_ids. */
  accessorKey: string
  /** Slug used to route the assign request to the correct endpoint. */
  tableTypeSlug: TableTypeSlug
}

export function getCampaignPushTarget(
  args: CampaignPushTargetArgs
): CampaignPushTarget {
  const accessorKey = args.isDoorsTable
    ? (args.doorProxyIdAccessorKey ?? 'id')
    : (args.chainProxyIdAccessorKey ?? 'id')

  return {
    accessorKey,
    tableTypeSlug: args.tableTypeSlug,
  }
}

/**
 * Count the distinct parent companies among selected door rows.
 *
 * Several doors of the same company collapse to one company when assigned to a
 * campaign, so the doors picker surfaces this count. Dedupes the rows by their
 * parent chain-proxy id; null/undefined ids are ignored. The result is always
 * <= the number of rows, and only meaningful for a fully-loaded selection
 * (the caller has every selected row in hand) (FIR-3963).
 *
 * @param rows - The selected door rows, loaded client-side.
 * @param chainProxyIdAccessorKey - Lodash path to a row's parent chain-proxy id.
 * @returns The number of distinct parent companies the rows resolve to.
 */
export function countDistinctCompanies(
  rows: unknown[],
  chainProxyIdAccessorKey: string
): number {
  return new Set(
    rows
      .map((row) => lodash.get(row as object, chainProxyIdAccessorKey))
      .filter((id) => id != null)
  ).size
}
